import { useRef, useState } from 'react';
import { AddRounded, ArrowDownwardRounded, CloseRounded, KeyboardArrowDownRounded, KeyboardArrowUpRounded } from '@mui/icons-material';
import { Box, Button, IconButton, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { FormTextField, HelpPopover, StatusBadge, designTokens } from '../../../design-system';
import { allowedSources, candidateFormatSegments, candidateParts, mappingCandidates, mappingFromCandidates } from '../model/mappings';
import type { ProfileMappingCandidate, ProfileMappingRule, SourcePropertyDefinition, TargetFieldDefinition } from '../model/types';
import { formatCandidateText, parseCandidateText } from '../model/mappingFormatText';
import { MappingRulePreview } from './MappingRulePreview';
import { MappingValueFormatEditor } from './MappingValueFormatEditor';

export function MappingRuleEditor({ target, rule, sources, disabled, onChange }: {
  target: TargetFieldDefinition;
  rule: ProfileMappingRule;
  sources: SourcePropertyDefinition[];
  disabled: boolean;
  onChange: (next: ProfileMappingRule, textState: { error?: string; dirty: boolean }) => void;
}) {
  const inputs = useRef(new Map<string, { focus: () => void }>());
  const addValueButton = useRef<HTMLButtonElement>(null);
  const initialTexts = useRef(mappingCandidates(rule).map((candidate) => formatCandidateText(candidate, sources)));
  const [rows, setRows] = useState(() => mappingCandidates(rule).map((candidate, index) => ({
    id: index, candidate, text: initialTexts.current[index], originalCandidate: candidate, originalText: initialTexts.current[index], conditionEdited: false, error: undefined as string | undefined,
  })));
  const nextRowId = useRef(rows.length);
  const candidates = rows.map((row) => row.candidate);
  const selectableSources = allowedSources(target, sources);
  const canCombine = !target.allowedModes || target.allowedModes.includes('JOIN_NON_EMPTY');
  const canChain = !target.allowedModes || target.allowedModes.includes('FALLBACK_CHAIN');
  const candidateSourceKeys = (candidate: ProfileMappingCandidate) => candidateParts(candidate).flatMap((part) => part.sourceProperty ? [part.sourceProperty] : []);
  const totalSources = candidates.reduce((total, candidate) => total + candidateSourceKeys(candidate).length, 0);
  const formatError = rows.find((row) => row.error)?.error;

  const focus = (key: string) => requestAnimationFrame(() => {
    (inputs.current.get(key) ?? addValueButton.current)?.focus();
  });
  const registerInput = (key: string, element: { focus: () => void } | null) => {
    if (element) inputs.current.set(key, element); else inputs.current.delete(key);
  };
  const publish = (next: typeof rows) => {
    if (disabled) return;
    setRows(next);
    onChange(mappingFromCandidates(target.key, next.map((row) => row.candidate)), {
      error: next.find((row) => row.error)?.error,
      dirty: JSON.stringify(next.map((row) => row.text)) !== JSON.stringify(initialTexts.current),
    });
  };
  const updateCandidate = (index: number, next: ProfileMappingCandidate, keepText = true) => {
    publish(rows.map((row, position) => position === index
      ? { ...row, candidate: next, ...(keepText ? { conditionEdited: true } : { text: formatCandidateText(next, sources), error: undefined }) } : row));
  };
  const updateText = (index: number, text: string) => {
    const previous = rows[index].candidate;
    const row = rows[index];
    const original = row.originalCandidate;
    const requireAll = row.conditionEdited ? previous.requireAll ?? false
      : original.parts || candidateSourceKeys(original).length > 1 ? original.requireAll ?? false : true;
    const parsed = parseCandidateText(text, selectableSources, requireAll);
    let next = parsed.candidate;
    if (!parsed.error && text === row.originalText && (!row.conditionEdited || Boolean(previous.requireAll) === Boolean(original.requireAll))) {
      next = original;
    } else if (!parsed.error && !original.parts) {
      const { sourceProperties, textGaps } = candidateFormatSegments(next);
      if (!textGaps[0] && !textGaps.at(-1) && textGaps.slice(1, -1).every((gap) => gap === (original.separator ?? ''))) {
        next = { ...original, sourceProperties, requireAll: row.conditionEdited ? previous.requireAll : original.requireAll };
      }
    }
    publish(rows.map((row, position) => position === index
      ? { ...row, text, error: parsed.error, candidate: parsed.error ? row.candidate : next } : row));
  };
  const moveCandidate = (index: number, offset: number) => {
    const destination = index + offset;
    if (disabled || destination < 0 || destination >= rows.length) return;
    const next = [...rows];
    [next[index], next[destination]] = [next[destination], next[index]];
    publish(next);
    focus(next[destination].id + ':0');
  };
  const removeCandidate = (index: number) => {
    publish(rows.filter((_, position) => position !== index));
    focus((rows[index + 1]?.id ?? rows[index - 1]?.id ?? -1) + ':0');
  };
  const addCandidate = () => {
    if (disabled || totalSources >= 6 || candidates.some((candidate) => candidateSourceKeys(candidate).length === 0)) return;
    const id = nextRowId.current++;
    publish([...rows, { id, candidate: { sourceProperties: [] }, text: '', originalCandidate: { sourceProperties: [] }, originalText: '', conditionEdited: false, error: undefined }]);
    focus(id + ':0');
  };

  return <Stack spacing={3} data-testid={`mapping-row-${target.key}`}>
    <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary">{target.description}</Typography>
      {target.required && <StatusBadge label="필수" tone="neutral" />}
    </Stack>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.15fr) minmax(0, 0.85fr)' }, gap: `${designTokens.space.lg}px`, alignItems: 'start' }}>
      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <Box>
          <Stack direction="row" sx={{ mb: 0.5, alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}><Typography component="h3" sx={designTokens.typography.subsectionTitle}>표시 형식</Typography><HelpPopover title="표시 형식">문자는 바로 입력하고 직원 속성은 원하는 위치에 추가하세요. 삽입한 속성을 누르면 변경할 수 있습니다.</HelpPopover></Stack>
            <Typography variant="caption" color="text.secondary">{totalSources} / 6개 속성</Typography>
          </Stack>
        </Box>
        <Stack component="ol" spacing={1} sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {rows.map((row, index) => {
            const candidate = row.candidate;
            const otherSources = canChain ? [] : candidates.flatMap((other, otherIndex) => otherIndex === index ? [] : candidateSourceKeys(other));
            return <Box component="li" key={row.id}>
              {index > 0 && <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'text.secondary', mb: 1, ml: 2 }}><ArrowDownwardRounded sx={{ fontSize: 16 }} /><Typography variant="caption">이전 값을 만들 수 없으면</Typography></Stack>}
              <Stack spacing={1.5} sx={{ p: `${designTokens.space.md}px`, border: `1px solid ${designTokens.color.divider}`, borderRadius: designTokens.radius.input }}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                    <Box component="span" sx={{ ...designTokens.typography.supporting, fontWeight: 600, color: designTokens.color.actionDark, bgcolor: designTokens.color.compactSelectedSurface, borderRadius: designTokens.radius.circle, width: 24, height: 24, display: 'grid', placeItems: 'center', flexShrink: 0 }}>{index + 1}</Box>
                    <Typography component="h4" sx={designTokens.typography.subsectionTitle}>{index === 0 ? '기본값' : `대체값 ${index}`}</Typography>
                    <HelpPopover title={`${index + 1}번째 값 사용 조건`}>{candidateSourceKeys(candidate).length < 2 ? '이 속성에 값이 있으면 사용합니다.' : candidate.requireAll ? '속성 중 하나라도 비어 있으면 아래 대체값을 사용합니다.' : candidate.parts ? '빈 속성은 생략하고, 직접 입력한 문자는 그대로 표시합니다.' : '값이 있는 속성만 조합합니다. 모두 비어 있으면 다음 값으로 넘어갑니다.'}</HelpPopover>
                  </Stack>
                  <Stack direction="row">
                    <Tooltip title="값을 위로"><span><IconButton size="small" aria-label={`${index + 1}번째 값 위로`} disabled={disabled || index === 0} onClick={() => moveCandidate(index, -1)}><KeyboardArrowUpRounded fontSize="small" /></IconButton></span></Tooltip>
                    <Tooltip title="값을 아래로"><span><IconButton size="small" aria-label={`${index + 1}번째 값 아래로`} disabled={disabled || index === candidates.length - 1} onClick={() => moveCandidate(index, 1)}><KeyboardArrowDownRounded fontSize="small" /></IconButton></span></Tooltip>
                    <Tooltip title="값 삭제"><span><IconButton size="small" aria-label={`${index + 1}번째 값 삭제`} disabled={disabled} onClick={() => {
                      removeCandidate(index);
                    }}><CloseRounded fontSize="small" /></IconButton></span></Tooltip>
                  </Stack>
                </Stack>
                <MappingValueFormatEditor index={index} candidate={candidate} text={row.text} error={row.error} target={target} sources={selectableSources} disabled={disabled}
                  canAddSource={totalSources < 6 && (candidateSourceKeys(candidate).length === 0 || (canCombine && (canChain || candidates.length === 1)))}
                  excludedSources={otherSources} registerInput={(key, element) => registerInput(row.id + ':' + key.split(':')[1], element)}
                  onTextChange={(text) => updateText(index, text)} onCandidateChange={(next) => next.sourceProperties.length ? updateCandidate(index, next, false) : removeCandidate(index)} />
                {candidateSourceKeys(candidate).length > 1 && <FormTextField select label={`${index + 1}번째 값의 사용 조건`} value={candidate.requireAll ? 'all' : 'any'} disabled={disabled || !canChain} onChange={(event) => updateCandidate(index, { ...candidate, requireAll: event.target.value === 'all' })}>
                  <MenuItem value="all">모든 속성에 값이 있을 때</MenuItem>
                  <MenuItem value="any">하나라도 값이 있을 때</MenuItem>
                </FormTextField>}
              </Stack>
            </Box>;
          })}
        </Stack>
        {(canChain || candidates.length === 0 || (candidates.every((candidate) => candidateSourceKeys(candidate).length === 1) && selectableSources.length > candidates.length)) && <Button
          ref={addValueButton} autoFocus={candidates.length === 0} variant="outlined" startIcon={<AddRounded />} onClick={addCandidate}
          disabled={disabled || totalSources >= 6 || candidates.some((candidate) => candidateSourceKeys(candidate).length === 0)} sx={{ alignSelf: 'flex-start' }}>
          {candidates.length ? '대체값 추가' : '사용할 값 추가'}
        </Button>}
      </Stack>
      <MappingRulePreview key={target.key} target={target} rule={rule} sources={sources} disabled={disabled} invalidReason={formatError} />
    </Box>
  </Stack>;
}
